
import { isAgent, isTrigger } from '@/common/chat';
import { ChatState, AgentInfo, ChatInfo } from './model';
import { sendToParent } from '@/common/bridge';
import { createNotice, deleteMessage, spawnChatMessage, spawnMediaMessage, spawnMessage } from '@/common/bot/lib';
import { MemberType, MessageType } from '@/common/bot/enum';
import { MessageListItem } from '@/common/bot/types';
import * as Locale from '@/plugins/i18n';
import { RequestUserTransactionMessage } from '@/common/bot/types/messages/RequestUserTransactionMessage';
import { analytics, EVENT_TYPES, GENERAL_EVENTS } from '@/common/logs';

const state: ChatState = {
  connection: 'closed',
  account_status: 'offline',
  departments: {},
  visitor: {},
  agents: {},
  chats: [],
  last_timestamp: 0,
  last_rating_request_timestamp: 0,
  has_rating: false,
  is_chatting: false,
  queue_position: 0,
  agentLeave: false,
  selectedFirstSubject: '', // used for logged in form open ticket
  selectedLastSubject: '', // used for logged in form open ticket
  selectedCategory: '',
  userTypingList: {},
  showTyping: false,
  is_ready_to_chat: false,
  hasViewArticle: false,
  hasSeenRate: false,
  agentJoined: false,
  extraTransactionInfo: [],
  hasNewMessage: false, // Only used for wap/ web
  isInitialized: false,
  arePastMsgsFromResumableChatCompleted: false,
};

const getters = {
  subject (state: ChatState) {
    return `${state.selectedCategory} + ${state.selectedLastSubject}`;
  },
};

const mutations = {
  updateTimeStamp (state: ChatState, timestamp: number) {
    state.last_timestamp = timestamp;
  },
  updateAccountStatus (state: ChatState, status: string) {
    state.account_status = status;
  },
  updateConnection (state: ChatState, status: string) {
    state.connection = status;
  },
  updateVistor (state: ChatState, visitorInfo: object) {
    state.visitor = {
      ...state.visitor,
      ...visitorInfo,
    };
  },
  updateAgent (state: any, agentInfo: AgentInfo) {
    state.agents = {
      ...state.agents,
      [agentInfo.nick]: {
        ...agentInfo,
        nick: agentInfo.nick, // To be removed after standardization
        typing: (state.agents[agentInfo.nick] || { typing: false }).typing,
      },
    };
  },
  updateAgentNickName (state: any, nickName: string) {
    if (state.agents[nickName]) {
      state.agents = {
        ...state.agents,
        [nickName]: {},
      };
    }
  },
  updateVistorNickName (state: ChatState, nickName: string) {
    state.visitor.nick = nickName;
  },
  addChats (state: any, chatInfo: MessageListItem) {
    state.chats = [...deleteMessage(state.chats, chatInfo), chatInfo];
  },
  updateChatting (state: ChatState, bool: boolean) {
    state.is_chatting = bool;
  },
  resetChat (state: ChatState) {
    state.connection = 'close';
    state.account_status = 'offline';
    state.departments = {};
    state.visitor = {};
    state.agents = {};
    state.chats = [];
    state.last_timestamp = 0;
    state.last_rating_request_timestamp = 0;
    state.has_rating = false;
    state.is_chatting = false;
    state.queue_position = 0;
    state.agentLeave = false;
    state.hasSeenRate = false;
    state.agentJoined = false;
  },
  updateQueuePosition (state: ChatState, queueNum: number) {
    state.queue_position = queueNum;
  },
  agentLeave (state: ChatState, bool: boolean) {
    state.agentLeave = bool;
  },
  updateSelectedSubject (state: ChatState, payload: {type: string, val: string}) {
    if (payload.type === 'first') {
      state.selectedFirstSubject = payload.val;
    } else if (payload.type === 'last') {
      state.selectedLastSubject = payload.val;
    }
  },
  updateSelectedCategory (state: ChatState, category: string) {
    state.selectedCategory = category;
  },
  updateTypingList (state: ChatState, list: Record<number, { isTyping: boolean; displayName: string; isAgent: boolean }>) {
    state.userTypingList = { ...state.userTypingList, ...list };
    const userIds = Object.keys(list);
    for (let i = 0; i < userIds.length; i++) {
      if (state.userTypingList[parseInt(userIds[i])].isAgent && state.userTypingList[parseInt(userIds[i])].isTyping) {
        state.showTyping = true;
        break;
      } else {
        state.showTyping = false;
      }
    }
  },
  readyToChat (state: ChatState, val: boolean) {
    state.is_ready_to_chat = val;
  },
  updateViewArticle (state: ChatState, val: boolean) {
    state.hasViewArticle = val;
  },
  updateHasSeenRate (state: ChatState, val: boolean) {
    state.hasSeenRate = val;
  },
  updateAgentJoined (state: ChatState, val: boolean) {
    state.agentJoined = val;
  },
  updateExtraTransactionInfo (state: ChatState, val: ChatState['extraTransactionInfo']) {
    state.extraTransactionInfo = val;
  },
  updateNewMessage (state: ChatState, val: boolean) {
    state.hasNewMessage = val;
  },
  updateInitialized (state: ChatState, val: boolean) {
    state.isInitialized = val;
  },
  updateArePastMsgsFromResumableChatCompleted (state: ChatState, val: boolean) {
    state.arePastMsgsFromResumableChatCompleted = val;
  },
};

const updateLastTimestamp = (commit: any, detail: any) => {
  if (detail.timestamp) {
    commit('updateTimeStamp', detail.timestamp);
  }
};

const actions = {
  account_status ({ commit } : { commit: Function}, payload: any) {
    updateLastTimestamp(commit, payload.detail);
    commit('updateAccountStatus', payload.detail);
  },
  connection_update ({ commit }: { commit: Function}, payload: any) {
    updateLastTimestamp(commit, payload.detail);
    commit('updateConnection', payload.detail);
  },
  department_update ({ commit }: { commit: Function}, payload: any) {
    updateLastTimestamp(commit, payload.detail);
  },
  visitor_update ({ commit }: { commit: Function}, payload: any) {
    updateLastTimestamp(commit, payload.detail);
    commit('updateVistor', payload.detail);
  },
  agent_update ({ commit }: { commit: Function}, payload: any) {
    updateLastTimestamp(commit, payload.detail);
    commit('updateAgent', payload.detail);
  },
  changeTypingList ({ commit }: { commit: Function}, payload: any) {
    commit('updateTypingList', payload);
  },
  readyToChat ({ commit }: { commit: Function}, payload: boolean) {
    commit('readyToChat', payload);
  },
  chat ({ commit }: { commit: Function}, payload: any) {
    updateLastTimestamp(commit, payload.detail);

    const chatType = payload.detail.type;

    if (chatType === 'chat.memberjoin') {
      if (isAgent(payload.detail.nick)) {
        commit('updateAgentNickName', payload.detail.nick);
        commit('updateAgentJoined', true);
      } else {
        commit('updateVistorNickName', payload.detail.nick);
      }
      if (!isAgent(payload.detail.nick)) {
        commit('updateChatting', true);
      }

      const joinMessage = Locale.i18n.t('notice.member.join', { name: payload.detail.display_name || 'Agent' }) as string;
      commit('addChats', createNotice({ message: joinMessage, id: joinMessage }));
    } else if (chatType === 'chat.memberleave') {
      if (!isAgent(payload.detail.nick)) {
        commit('updateChatting', false);
      } else {
        commit('agentLeave', true);
      }

      const leaveMessage = Locale.i18n.t('notice.member.leave', { name: payload.detail.display_name || 'Agent' }) as string;
      commit('addChats', createNotice({ message: leaveMessage, id: leaveMessage }));
    } else if (chatType === 'chat.queue_position') {
      commit('updateQueuePosition', payload.detail.queue_position);
    } else if (chatType === 'chat.request.rating') {

    } else if (chatType === 'chat.file' || chatType === 'chat.msg' || chatType === 'chat.sys.browse') {
      if (payload.detail && payload.detail.options && payload.detail.options.length) return; // disable zendesk options for now
      const newState = payload.detail;
      if (isTrigger(newState.nick)) {
        newState.nick = `agent:trigger:${payload.detail.display_name}`;
      }

      if (chatType === 'chat.msg') {
        if (!state.arePastMsgsFromResumableChatCompleted && payload?.detail?.resumableChatSessionId === payload?.detail?.packetResumableChatSessionId) {
          commit('updateArePastMsgsFromResumableChatCompleted', true);
        }
        if (!payload?.detail?.resumableChatSessionId || (state.arePastMsgsFromResumableChatCompleted)) {
          analytics.track({
            type: EVENT_TYPES.CHAT_MSG,
            name: GENERAL_EVENTS.LIVE_CHAT_MSG_SEQUENCE,
            selection: payload?.detail?.chatId,
            metadata: { message: payload?.detail?.msg || '' },
          });
        }
      }
      newState.member_type = isAgent(newState.nick) ? 'agent' : 'visitor';
      sendToParent('NEW_MESSAGE');
      commit('updateNewMessage', true);

      const memberType = isAgent(newState.nick) ? MemberType.AGENT : MemberType.VISITOR;

      if (chatType === 'chat.file') {
        const message = spawnMediaMessage({
          memberType,
          attachment: { ...newState.attachment, mimeType: newState.attachment.mime_type },
        });

        commit('addChats', message);
        return;
      }
      if (payload.detail.meta) {
        // This is a special message that denotes it's type.
        if ([MessageType.AGENT_REQUEST_INPUT, MessageType.USER_SUBMIT_INPUT_TRANSACTION].includes(payload.detail.meta.messageKind)) {
          // We only target the transaction types now, factories can be created for different type of messages.
          const message = spawnMessage<RequestUserTransactionMessage>({
            type: payload.detail.meta.messageKind,
            memberType,
            message: newState.msg,
            timestamp: new Date(newState.timestamp).getMilliseconds(),
            options: payload.detail.meta.options,
            transactionKind: payload.detail.meta.transactionKind,
          });

          commit('addChats', message);
          return;
        }
      }
      // For normal messages
      const message = spawnChatMessage({
        memberType,
        message: newState.msg,
        timestamp: new Date(newState.timestamp).getMilliseconds(),
      });
      commit('addChats', message);
    } else if (chatType === 'typing') {
      // let agent = state.agents[payload.detail.nick];
    }
  },
  // visitor_send_msg ({ commit }: { commit: Function}, payload: any) {
  //   updateLastTimestamp(commit, payload.detail);
  // },
  error (state: any, payload: any) {
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
